/**
 * Serviço de API para gestão de convites de tenants
 * Refatorado para usar apiService centralizado (Auth + Error Handling)
 */

import { apiService } from './api'

export interface TenantInvite {
    id: number
    email: string
    token: string
    status: 'pending' | 'accepted' | 'expired' | 'cancelled'
    plan_details?: {
        plan_name?: string
        monthly_price?: string
    }
    created_at: string
    expires_at: string
    is_valid: boolean
}

export interface CreateInvitePayload {
    email: string
    plan_details?: {
        plan_name?: string
        monthly_price?: string
    }
}

export interface CreateInviteResponse {
    message: string
    token: string
    link: string
    expires_at: string
}

class InvitesService {
    /**
     * Lista todos os convites (Admin)
     */
    async listInvites(): Promise<{ invites: TenantInvite[] }> {
        return apiService.request<{ invites: TenantInvite[] }>('/api/admin/invites')
    }

    /**
     * Cria um novo convite (Admin)
     */
    async createInvite(payload: CreateInvitePayload): Promise<CreateInviteResponse> {
        return apiService.request<CreateInviteResponse>('/api/admin/invites', {
            method: 'POST',
            body: JSON.stringify(payload),
        })
    }

    /**
     * Valida um token de convite (Público)
     */
    async validateToken(token: string): Promise<{
        valid: boolean
        email: string
        plan_details?: { plan_name?: string; monthly_price?: string }
    }> {
        return apiService.request(`/api/public/invites/${token}`)
    }

    /**
     * Valida um link fixo de indicação B2B (Público)
     */
    async validateTenantReferral(token: string): Promise<{
        valid: boolean
        inviter_name: string
    }> {
        return apiService.request(`/api/public/tenant-referrals/${token}`)
    }

    /**
     * Consulta CNPJ via BrasilAPI (Público)
     */
    async consultCNPJ(cnpj: string): Promise<{
        cnpj: string
        razao_social: string
        nome_fantasia?: string
        logradouro?: string
        numero?: string
        complemento?: string
        bairro?: string
        cep?: string
        municipio?: string
        uf?: string
        email?: string
        telefone?: string
    }> {
        return apiService.request(`/api/public/cnpj/${cnpj.replace(/\D/g, '')}`)
    }

    /**
     * Registra um novo tenant via convite (Público)
     */
    async registerTenant(data: {
        token?: string
        b2b_ref?: string
        name: string
        tenant_type: 'PF' | 'PJ'
        email?: string
        admin_username: string
        admin_email: string
        admin_password: string
        cnpj?: string
        company_name?: string
        trade_name?: string
        cpf?: string
        full_name?: string
        phone?: string
        zip_code?: string
        street?: string
        number?: string
        complement?: string
        neighborhood?: string
        city?: string
        state?: string
    }): Promise<{
        message: string
        tenant_id: number
        redirect_url: string
    }> {
        return apiService.request('/api/public/tenants/register', {
            method: 'POST',
            body: JSON.stringify(data),
        })
    }
}

export const invitesService = new InvitesService()
